import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SAVE_APP_SETTINGS_MUTATION } from '../../../../settings/graphql/mutations';
import { GET_APP_SETTINGS_QUERY } from '../../../../settings/graphql/queries';
import { useCurrentUser } from '../../../../user/hooks/useCurrentUser';
import { SETTINGS_TAB } from '../constants';

export function useColumnVisibility<T extends Record<string, boolean>>(
  colVisKey: string,
  initialValue: T
) {
  const currentUser = useCurrentUser();
  const currentUserId = currentUser.data?.id || null;

  const initialValueRef = useRef(initialValue);
  const [visibleColumns, setVisibleColumns] = useState<T>(initialValue);

  const { data: settingsData } = useQuery(GET_APP_SETTINGS_QUERY, {
    variables: { input: { settingsTab: SETTINGS_TAB } },
  });

  const [saveSettings] = useMutation(SAVE_APP_SETTINGS_MUTATION, {
    refetchQueries: [
      {
        query: GET_APP_SETTINGS_QUERY,
        variables: { input: { settingsTab: SETTINGS_TAB } },
      },
    ],
  });

  useEffect(() => {
    if (settingsData?.getAppSettings?.settingsJson && currentUserId) {
      const json = settingsData.getAppSettings.settingsJson as Record<
        string,
        unknown
      >;
      const allUserOpts = json.userColumnVisibility as
        | Record<string, Record<string, Partial<Record<string, boolean>>>>
        | undefined;
      const userOpts = allUserOpts?.[currentUserId]?.[colVisKey];
      if (userOpts)
        setVisibleColumns({ ...initialValueRef.current, ...userOpts } as T);
    }
  }, [settingsData, currentUserId, colVisKey]);

  const handleColumnVisibilityChange = useCallback(
    (key: string, checked: boolean) => {
      const next = { ...visibleColumns, [key]: checked };
      setVisibleColumns(next);
      if (!currentUserId) return;
      const existing =
        (settingsData?.getAppSettings?.settingsJson as Record<
          string,
          unknown
        >) || {};
      const allUserVis =
        (existing.userColumnVisibility as Record<string, unknown>) || {};
      const userVis =
        (allUserVis[currentUserId] as Record<string, unknown>) || {};
      saveSettings({
        variables: {
          input: {
            settingsTab: SETTINGS_TAB,
            settingsJson: {
              ...existing,
              userColumnVisibility: {
                ...allUserVis,
                [currentUserId]: {
                  ...userVis,
                  [colVisKey]: next,
                },
              },
            },
          },
        },
      });
    },
    [visibleColumns, currentUserId, settingsData, saveSettings, colVisKey]
  );

  return { visibleColumns, handleColumnVisibilityChange };
}
